import { logger } from 'module/logger';
import {
  SignalSummaryValue,
  SignalSummaryData,
  DetailedSummaryItem,
} from 'api/signal-occurrences/types';
import moment from 'moment';

export type GroupByType = 'MONTH' | 'WEEK' | 'QUARTER';

/** Max width for the main reading column only (not headers or left period/date labels). */
export const SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX = 768;

// WeakMap to store period keys for items
const itemPeriodKeyMap = new WeakMap<SignalSummaryValue, string>();

// Helper to store period key for an item
export const setPeriodKeyForItem = (
  item: SignalSummaryValue,
  periodKey: string,
): void => {
  itemPeriodKeyMap.set(item, periodKey);
};

// Helper to get period key from data item based on groupBy
export const getPeriodKeyFromItem = (
  item: SignalSummaryValue,
  groupBy: GroupByType,
  periodKeyOverride?: string,
): string => {
  // If periodKeyOverride is provided, use it directly
  if (periodKeyOverride) {
    return periodKeyOverride;
  }

  // Try to get from WeakMap
  const storedKey = itemPeriodKeyMap.get(item);

  if (storedKey) {
    return storedKey;
  }

  // If not found, return empty (should not happen in normal flow)
  logger.warn('getPeriodKeyFromItem: period key not found for item', {
    item,
    groupBy,
  });

  return '';
};

// Helper to merge SignalSummaryData entries
export const mergeSignalSummaryData = (
  entries: SignalSummaryData[],
): SignalSummaryData => {
  if (entries.length === 0) {
    throw new Error('Cannot merge empty array');
  }

  if (entries.length === 1) {
    return entries[0];
  }

  // Combine crispSummary (join with newlines)
  const combinedCrispSummary = entries
    .map((entry) => entry.crispSummary)
    .filter((summary) => summary && summary.trim())
    .join('\n\n');

  // For detailedSummary, combine them
  const allDetailedSummaries: DetailedSummaryItem[] = [];
  const seenTitles = new Set<string>();

  entries.forEach((entry) => {
    let items: DetailedSummaryItem[] = [];

    if (Array.isArray(entry.detailedSummary)) {
      items = entry.detailedSummary;
    } else if (
      typeof entry.detailedSummary === 'object' &&
      entry.detailedSummary !== null
    ) {
      // Convert object to array format
      items = Object.entries(entry.detailedSummary).map(([title, content]) => ({
        title: title.startsWith('*') ? title.substring(1).trim() : title.trim(),
        content: typeof content === 'string' ? content : String(content),
      }));
    }

    items.forEach((item) => {
      const key = item.title.toLowerCase().trim();

      if (!seenTitles.has(key)) {
        seenTitles.add(key);
        allDetailedSummaries.push(item);
      } else {
        const existing = allDetailedSummaries.find(
          (existingItem) => existingItem.title.toLowerCase().trim() === key,
        );

        if (existing) {
          existing.content = `${existing.content}\n\n${item.content}`;
        }
      }
    });
  });

  return {
    crispSummary: combinedCrispSummary,
    detailedSummary: allDetailedSummaries,
  };
};

// Helper to get period key for an offset from current
export const getPeriodKeyByOffset = (
  offset: number,
  groupBy: GroupByType,
): string => {
  const date = moment();

  switch (groupBy) {
    case 'WEEK': {
      const targetDate = date.subtract(offset, 'weeks');

      return `${targetDate.isoWeekYear()}-W${targetDate
        .isoWeek()
        .toString()
        .padStart(2, '0')}`;
    }
    case 'QUARTER': {
      const targetDate = date.subtract(offset, 'quarters');
      const quarter = Math.floor(targetDate.month() / 3) + 1;

      return `${targetDate.year()}-Q${quarter}`;
    }
    case 'MONTH':
    default:
      return date.subtract(offset, 'months').format('YYYY-MM');
  }
};

// Helper to get start of period for a given period key
export const getPeriodStart = (
  periodKey: string,
  groupBy: GroupByType,
): moment.Moment => {
  switch (groupBy) {
    case 'WEEK': {
      const match = periodKey.match(/^(\d{4})-W(\d{2})$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const week = parseInt(match[2], 10);

        // Use ISO week methods to correctly handle year boundaries
        return moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
      }

      return moment(periodKey).startOf('isoWeek');
    }
    case 'QUARTER': {
      const match = periodKey.match(/^(\d{4})-Q(\d)$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const quarter = parseInt(match[2], 10);

        return moment([year, (quarter - 1) * 3]).startOf('quarter');
      }

      return moment(periodKey).startOf('quarter');
    }
    case 'MONTH':
    default: {
      return moment(periodKey, 'YYYY-MM').startOf('month');
    }
  }
};

// Helper to get date range for a single period
export const getDateRangeForPeriod = (
  periodKey: string,
  groupBy: GroupByType,
): { startDate: string; endDate: string } => {
  let startDate: moment.Moment;
  let endDate: moment.Moment;

  switch (groupBy) {
    case 'WEEK': {
      const match = periodKey.match(/^(\d{4})-W(\d{2})$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const week = parseInt(match[2], 10);

        // Use ISO week methods to correctly handle year boundaries
        startDate = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
        endDate = moment().isoWeekYear(year).isoWeek(week).endOf('isoWeek');
      } else {
        startDate = moment(periodKey).startOf('isoWeek');
        endDate = moment(periodKey).endOf('isoWeek');
      }

      const today = moment().endOf('day');

      if (endDate.isAfter(today)) {
        endDate = today.clone();
      }

      break;
    }
    case 'QUARTER': {
      const match = periodKey.match(/^(\d{4})-Q(\d)$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const quarter = parseInt(match[2], 10);

        startDate = moment([year, (quarter - 1) * 3]).startOf('quarter');
        endDate = moment([year, (quarter - 1) * 3]).endOf('quarter');
      } else {
        startDate = moment(periodKey).startOf('quarter');
        endDate = moment(periodKey).endOf('quarter');
      }

      const today = moment().endOf('day');

      if (endDate.isAfter(today)) {
        endDate = today.clone();
      }

      break;
    }
    case 'MONTH':
    default: {
      startDate = moment(periodKey, 'YYYY-MM').clone().startOf('month');
      endDate = moment(periodKey, 'YYYY-MM').clone().endOf('month');

      const today = moment().endOf('day');

      if (endDate.isAfter(today)) {
        endDate = today.clone();
      }

      break;
    }
  }

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  };
};

// Helper to add/subtract periods
export const addPeriods = (
  periodKey: string,
  amount: number,
  groupBy: GroupByType,
): string => {
  let date: moment.Moment;

  switch (groupBy) {
    case 'WEEK': {
      const match = periodKey.match(/^(\d{4})-W(\d{2})$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const week = parseInt(match[2], 10);

        // Use ISO week methods to correctly handle year boundaries
        date = moment().isoWeekYear(year).isoWeek(week).add(amount, 'weeks');
      } else {
        date = moment(periodKey).add(amount, 'weeks');
      }

      // Format using ISO week year and week number
      return `${date.isoWeekYear()}-W${date.isoWeek().toString().padStart(2, '0')}`;
    }
    case 'QUARTER': {
      const match = periodKey.match(/^(\d{4})-Q(\d)$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const quarter = parseInt(match[2], 10);

        date = moment([year, (quarter - 1) * 3]).add(amount, 'quarters');
      } else {
        date = moment(periodKey).add(amount, 'quarters');
      }

      const quarter = Math.floor(date.month() / 3) + 1;

      return `${date.year()}-Q${quarter}`;
    }
    case 'MONTH':
    default: {
      date = moment(periodKey, 'YYYY-MM').add(amount, 'months');

      return date.format('YYYY-MM');
    }
  }
};

// Helper to compare periods (returns -1, 0, or 1)
export const comparePeriods = (
  periodKey1: string,
  periodKey2: string,
  groupBy: GroupByType,
): number => {
  const date1 = getPeriodStart(periodKey1, groupBy);
  const date2 = getPeriodStart(periodKey2, groupBy);

  if (date1.isBefore(date2)) {
    return -1;
  }

  if (date1.isAfter(date2)) {
    return 1;
  }

  return 0;
};

// Helper to get date range for multiple periods
export const getDateRangeForPeriods = (
  startPeriodKey: string,
  endPeriodKey: string,
  groupBy: GroupByType,
) => {
  const startRange = getDateRangeForPeriod(startPeriodKey, groupBy);
  const endRange = getDateRangeForPeriod(endPeriodKey, groupBy);

  return {
    startDate: startRange.startDate,
    endDate: endRange.endDate,
  };
};

// Helper to get timeline period dates
export const getTimelinePeriodDates = (
  periodKey: string,
  groupBy: GroupByType,
): { startTime: string; endTime: string; label: string } => {
  let startDate: moment.Moment;
  let endDate: moment.Moment;
  let label: string;

  switch (groupBy) {
    case 'WEEK': {
      const match = periodKey.match(/^(\d{4})-W(\d{2})$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const week = parseInt(match[2], 10);

        // Use ISO week methods to correctly handle year boundaries
        startDate = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
        endDate = moment().isoWeekYear(year).isoWeek(week).endOf('isoWeek');
      } else {
        startDate = moment(periodKey).startOf('isoWeek');
        endDate = moment(periodKey).endOf('isoWeek');
      }

      const today = moment().endOf('day');

      if (endDate.isAfter(today)) {
        endDate = today.clone();
      }

      const weekStart = startDate.clone();
      const weekEnd = endDate.clone();

      if (weekStart.month() === weekEnd.month()) {
        label = `${weekStart.format('MMM D')} - ${weekEnd.format('D')}`;
      } else {
        label = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`;
      }
      break;
    }
    case 'QUARTER': {
      const match = periodKey.match(/^(\d{4})-Q(\d)$/);

      if (match) {
        const year = parseInt(match[1], 10);
        const quarter = parseInt(match[2], 10);

        startDate = moment([year, (quarter - 1) * 3]).startOf('quarter');
        endDate = moment([year, (quarter - 1) * 3]).endOf('quarter');
      } else {
        startDate = moment(periodKey).startOf('quarter');
        endDate = moment(periodKey).endOf('quarter');
      }

      const today = moment().endOf('day');

      if (endDate.isAfter(today)) {
        endDate = today.clone();
      }

      const quarterNum = Math.floor(startDate.month() / 3) + 1;

      label = `Q${quarterNum} ${startDate.format('YYYY')}`;
      break;
    }
    case 'MONTH':
    default: {
      startDate = moment(periodKey, 'YYYY-MM').startOf('month');
      endDate = moment(periodKey, 'YYYY-MM').endOf('month');

      const today = moment().endOf('day');

      if (endDate.isAfter(today)) {
        endDate = today.clone();
      }

      label = startDate.format('MMM YYYY');
      break;
    }
  }

  return {
    startTime: startDate.format('YYYY-MM-DD'),
    endTime: endDate.format('YYYY-MM-DD'),
    label,
  };
};

// Helper to find period index in data array
export const findPeriodIndex = (
  data: SignalSummaryValue[],
  periodKey: string,
  groupBy: GroupByType,
): number => {
  return data.findIndex(
    (item) => getPeriodKeyFromItem(item, groupBy) === periodKey,
  );
};

// Helper to merge and deduplicate period data
export const mergePeriodData = (
  existing: SignalSummaryValue[],
  newDataWithPeriodKeys: Array<{ item: SignalSummaryValue; periodKey: string }>,
  groupBy: GroupByType,
): { merged: SignalSummaryValue[]; periodKeys: Set<string> } => {
  const periodMap = new Map<string, SignalSummaryValue>();

  // Add existing data
  existing.forEach((item) => {
    const key = getPeriodKeyFromItem(item, groupBy);

    if (key) {
      if (!periodMap.has(key)) {
        periodMap.set(key, item);
      } else {
        // Period already exists, merge signal types
        const existingItem = periodMap.get(key)!;

        // Merge Risk if present
        if (item.Risk && !existingItem.Risk) {
          existingItem.Risk = item.Risk;
        } else if (item.Risk && existingItem.Risk) {
          existingItem.Risk = mergeSignalSummaryData([
            existingItem.Risk,
            item.Risk,
          ]);
        }

        // Merge Opportunity if present
        if (item.Opportunity && !existingItem.Opportunity) {
          existingItem.Opportunity = item.Opportunity;
        } else if (item.Opportunity && existingItem.Opportunity) {
          existingItem.Opportunity = mergeSignalSummaryData([
            existingItem.Opportunity,
            item.Opportunity,
          ]);
        }

        // Merge Operational if present
        if (item.Operational && !existingItem.Operational) {
          existingItem.Operational = item.Operational;
        } else if (item.Operational && existingItem.Operational) {
          existingItem.Operational = mergeSignalSummaryData([
            existingItem.Operational,
            item.Operational,
          ]);
        }
      }
    } else {
      logger.warn('Skipping item with invalid period key', {
        item,
        groupBy,
      });
    }
  });

  // Add new data
  newDataWithPeriodKeys.forEach(({ item, periodKey }) => {
    const key = periodKey;

    if (!key) {
      logger.warn('Skipping new item with invalid period key', {
        item,
        periodKey,
        groupBy,
      });

      return;
    }

    if (periodMap.has(key)) {
      // Merge signal types within the same period instead of overwriting
      const existingItem = periodMap.get(key)!;

      // Merge Risk if present
      if (item.Risk && !existingItem.Risk) {
        existingItem.Risk = item.Risk;
      } else if (item.Risk && existingItem.Risk) {
        existingItem.Risk = mergeSignalSummaryData([
          existingItem.Risk,
          item.Risk,
        ]);
      }

      // Merge Opportunity if present
      if (item.Opportunity && !existingItem.Opportunity) {
        existingItem.Opportunity = item.Opportunity;
      } else if (item.Opportunity && existingItem.Opportunity) {
        existingItem.Opportunity = mergeSignalSummaryData([
          existingItem.Opportunity,
          item.Opportunity,
        ]);
      }

      // Merge Operational if present
      if (item.Operational && !existingItem.Operational) {
        existingItem.Operational = item.Operational;
      } else if (item.Operational && existingItem.Operational) {
        existingItem.Operational = mergeSignalSummaryData([
          existingItem.Operational,
          item.Operational,
        ]);
      }
    } else {
      // New period, just add it
      periodMap.set(key, item);
    }
  });

  // Convert to array and sort by period (newest first)
  const mergedWithKeys = Array.from(periodMap.entries())
    .map(([periodKey, item]) => ({ periodKey, item }))
    .sort((a, b) => {
      const dateA = getPeriodStart(a.periodKey, groupBy);
      const dateB = getPeriodStart(b.periodKey, groupBy);

      return dateB.valueOf() - dateA.valueOf();
    });

  // Store period keys in WeakMap for later retrieval
  mergedWithKeys.forEach(({ periodKey, item }) => {
    setPeriodKeyForItem(item, periodKey);
  });

  const merged = mergedWithKeys.map(({ item }) => item);
  const periodKeys = new Set(periodMap.keys());

  logger.info('Merged period data', {
    existingCount: existing.length,
    newDataCount: newDataWithPeriodKeys.length,
    mergedCount: merged.length,
    periodKeys: Array.from(periodKeys),
  });

  return { merged, periodKeys };
};

// Helper to merge timeline periods
export const mergeTimelinePeriods = <T extends { periodKey: string }>(
  existing: T[],
  newData: T[],
): T[] => {
  const periodMap = new Map<string, T>();

  existing.forEach((period) => {
    periodMap.set(period.periodKey, period);
  });

  newData.forEach((period) => {
    periodMap.set(period.periodKey, period);
  });

  return Array.from(periodMap.values()).sort((a, b) => {
    const dateA = moment((a as any).startTime);
    const dateB = moment((b as any).startTime);

    return dateB.diff(dateA);
  });
};
